import {
  ChangeList,
  calendarDay,
  dayRange,
  formatDateTime,
  ListView,
  PageHeader,
  TextButton,
  useCursorList,
} from "@majitap/ui";
import { DateRangePicker } from "@majitap/ui/arc/date-range-picker/date-range-picker";
import { Select } from "@majitap/ui/arc/select/select";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { api, errorOf, ok } from "../../api";
import { useCompanyFormat } from "../../company";
import { messageFor } from "../../error-copy";
import { filterSearch } from "../../list-search";
import { RouterLink } from "../../router-link";
import { type Activity, allStaffQuery, targetHref } from "../../team";
import styles from "./settings.module.css";

/** The kinds of record the log covers, as people call them. */
const TARGETS: Record<string, string> = {
  order: "Orders",
  delivery: "Deliveries",
  customer: "Customers",
  business: "Businesses",
  partner: "Partners",
  truck: "Trucks",
  driver: "Drivers",
  driver_assistant: "Assistants",
  driver_shift: "Shifts",
  fillsite: "Fill sites",
  location: "Places",
  tank: "Tanks",
  sensor: "Tank sensors",
  incident: "Incidents",
  incident_type: "Incident types",
  invoice: "Invoices",
  payment: "Payments",
  wallet: "Wallets",
  payout_request: "Payouts",
  provider_transaction: "Till payments",
  payment_term: "Payment terms",
  product: "Products",
  pricing: "Pricing rules",
  charge_type: "Charge types",
  voucher: "Vouchers",
  geofence: "Service zones",
  staff: "Staff",
  role: "Roles",
  integration: "Integrations",
  api_token: "API tokens",
  webhook_endpoint: "Webhooks",
  webhook_delivery: "Webhook deliveries",
  outbox_event: "Failed events",
  readiness: "Sign-in reminders",
  category: "Categories",
  file: "Files",
};

export const auditFilters = filterSearch({
  target_type: "text",
  // one record's history, from a link
  target_id: "text",
  actor_id: "text",
  from: "text",
  to: "text",
});

/** "Updated: name, phone" from an entry's verb and changed fields. */
export function entryText(a: Pick<Activity, "verb" | "diff">): string {
  const verb = a.verb.replace(/_/g, " ");
  const fields = Object.keys(a.diff ?? {}).map((f) => f.replace(/_/g, " "));
  return `${verb.charAt(0).toUpperCase()}${verb.slice(1)}${fields.length ? `: ${fields.join(", ")}` : ""}`;
}

/** The audit log (P8-22): who changed what, when; filter by the kind of record, who and when. */
export function AuditPage(): ReactNode {
  const search = useSearch({ from: "/console/settings/audit" });
  const navigate = useNavigate({ from: "/settings/audit" });
  const format = useCompanyFormat();
  const staff = useQuery(allStaffQuery);
  const list = useCursorList({
    queryKey: ["audit", search],
    fetchPage: (cursor) => ok(api.GET("/v1/admin/audit", { params: { query: { ...search, cursor, limit: 50 } } })),
  });
  const set = (patch: Partial<typeof search>) => navigate({ search: (s) => ({ ...s, ...patch }) });
  return (
    <>
      <PageHeader title="Audit log" description="Who changed what, and when. Nothing here can be edited or removed." />
      <ListView
        caption="Audit log"
        itemName={{ one: "entry", other: "entries" }}
        rows={list.rows}
        rowKey={(a) => a.id}
        status={list.status}
        errorMessage={list.error ? messageFor(errorOf(list.error)) : undefined}
        onRetry={list.refetch}
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        onLoadMore={list.loadMore}
        filtered={auditFilters.active(search).length > 0}
        onResetFilters={() => navigate({ search: {} })}
        empty={{ title: "Nothing yet", description: "Changes people make show here." }}
        toolbar={
          <div className={styles.toolbar}>
            <Select
              label="Record"
              value={search.target_type ?? "any"}
              onValueChange={(v) => set({ target_type: v === "any" ? undefined : v })}
              options={[
                { value: "any", label: "Any record" },
                ...Object.entries(TARGETS).map(([value, label]) => ({ value, label })),
              ]}
            />
            <Select
              label="Who"
              value={search.actor_id ?? "anyone"}
              onValueChange={(v) => set({ actor_id: v === "anyone" ? undefined : v })}
              options={[
                { value: "anyone", label: "Anyone" },
                ...(staff.data ?? []).map((s) => ({ value: s.user.id, label: s.user.name })),
              ]}
            />
            <DateRangePicker
              label="When"
              placeholder="Any time"
              value={
                search.from && search.to
                  ? {
                      start: calendarDay(search.from, format.timezone),
                      end: calendarDay(new Date(Date.parse(search.to) - 1).toISOString(), format.timezone),
                    }
                  : null
              }
              onChange={(range) => set(dayRange(range, format.timezone))}
            />
            {search.target_id ? (
              <span className={styles.muted}>
                One record's history. <TextButton onClick={() => set({ target_id: undefined })}>All records</TextButton>
              </span>
            ) : null}
          </div>
        }
        columns={[
          {
            key: "created_at",
            label: "When",
            render: (a) => <span className={styles.muted}>{formatDateTime(a.created_at, format)}</span>,
          },
          { key: "actor", label: "Who", render: (a) => a.actor?.name || (a.actor_user_id ? "Someone" : "The system") },
          {
            key: "target",
            label: "Record",
            render: (a) => {
              const href = targetHref(a.target_type, a.target_id);
              const label = TARGETS[a.target_type]?.replace(/s$/, "") ?? a.target_type.replace(/_/g, " ");
              return href ? (
                <RouterLink href={href} className={styles.link}>
                  {label}
                </RouterLink>
              ) : (
                label
              );
            },
          },
          {
            key: "verb",
            label: "What",
            render: (a) => (
              <span className={styles.stack}>
                <span>{entryText(a)}</span>
                {Object.keys(a.diff ?? {}).length > 3 ? (
                  <details className={styles.details}>
                    <summary>Show the changes</summary>
                    <ChangeList diff={a.diff} />
                  </details>
                ) : (
                  <ChangeList diff={a.diff} />
                )}
              </span>
            ),
          },
        ]}
      />
    </>
  );
}
